<template>
  <main class="login-page">
    <div class="login-backdrop" aria-hidden="true"></div>

    <section class="login-shell" aria-labelledby="login-title">
      <aside class="brand-panel" :aria-label="$t('爱精益工作台')">
        <div class="brand-panel__content">
          <div class="brand-lockup">
            <img class="brand-logo" src="@/assets/logo.png" alt="">
            <div>
              <p class="brand-name">ILEAN</p>
              <p class="brand-name-cn">{{ $t("爱精益") }}</p>
            </div>
          </div>

          <div class="brand-copy">
            <p class="brand-eyebrow">{{ $t("精益改善工作台") }}</p>
            <h1>{{ $t("把每一次改进，") }}<br>{{ $t("都推进到结果。") }}</h1>
            <p class="brand-description">
              {{ $t("从问题记录到对策验证，让团队在同一个清晰的工作流中持续改善。") }}
            </p>
          </div>

          <ol class="improvement-path" :aria-label="$t('改善流程')">
            <li>
              <span class="path-index">01</span>
              <span>{{ $t("发现问题") }}</span>
            </li>
            <li>
              <span class="path-index">02</span>
              <span>{{ $t("推进对策") }}</span>
            </li>
            <li>
              <span class="path-index">03</span>
              <span>{{ $t("验证结果") }}</span>
            </li>
          </ol>
        </div>
      </aside>

      <div class="login-panel">
        <div class="mobile-brand" :aria-label="$t('爱精益')">
          <img src="@/assets/logo.png" alt="">
          <div>
            <strong>ILEAN</strong>
            <span>{{ $t("爱精益") }}</span>
          </div>
        </div>

        <div class="login-card">
          <header class="login-heading">
            <p class="login-kicker">{{ $t("欢迎回来") }}</p>
            <h2 id="login-title">{{ $t("登录爱精益") }}</h2>
            <p>{{ $t("使用手机号和短信验证码进入工作台") }}</p>
          </header>

          <form class="login-form" novalidate @submit.prevent="login">
            <div class="field-group">
              <label class="field-label" for="phone_number_input">{{ $t("手机号") }}</label>
              <span class="field-control">
                <i class="el-icon-mobile-phone" aria-hidden="true"></i>
                <input
                  id="phone_number_input"
                  :value="phoneNumber"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="tel-national"
                  autofocus
                  :placeholder="$t('请输入 11 位手机号')"
                  @input="updatePhoneNumber"
                  @change="updatePhoneNumber"
                >
              </span>
            </div>

            <div class="field-group">
              <label class="field-label" for="sms_code_input">{{ $t("验证码") }}</label>
              <span class="field-control field-control--code">
                <i class="el-icon-chat-dot-round" aria-hidden="true"></i>
                <input
                  id="sms_code_input"
                  v-model="smsCode"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  :placeholder="$t('请输入 6 位验证码')"
                  @input="smsCode = digitsOnly(smsCode, 6)"
                >
                <button
                  class="sms-button"
                  type="button"
                  :disabled="smsLoading || isSend"
                  @click="getSmsCode"
                >
                  <i v-if="smsLoading" class="el-icon-loading" aria-hidden="true"></i>
                  <span>{{ isSend ? message : (smsLoading ? $t("发送中") : $t("获取验证码")) }}</span>
                </button>
              </span>
            </div>

            <button class="login-button" type="submit" :disabled="loginLoading">
              <span>{{ loginLoading ? $t("正在登录") : $t("进入工作台") }}</span>
              <i :class="loginLoading ? 'el-icon-loading' : 'el-icon-right'" aria-hidden="true"></i>
            </button>
          </form>

          <p class="login-note">
            {{ $t("验证码将在 60 秒内发送，请留意手机短信。") }}
          </p>
        </div>

        <footer class="login-footer">
          <span>{{ $t("© {p0} 爱精益", { p0: currentYear }) }}</span>
          <span class="footer-divider" aria-hidden="true"></span>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            粤ICP备19153829号
          </a>
        </footer>
      </div>
    </section>
  </main>
</template>

<script>
import { t } from "@/i18n";
import axios from 'axios';
import Axios from 'axios';
import { isTokenExpired, refreshToken } from '@/utils/refreshToken';

let timer = null;

export default {
  name: 'Login',
  data() {
    return {
      phoneNumber: '',
      smsCode: '',
      isSend: false,
      message: '',
      smsLoading: false,
      loginLoading: false,
      currentYear: new Date().getFullYear(),
    };
  },
  async created() {
    await this.tryAutoLogin();
  },
  beforeDestroy() {
    clearInterval(timer);
    timer = null;
  },
  methods: {
    updatePhoneNumber(event) {
      const digits = String(event.target.value || '').replace(/\D/g, '');
      // Strip the country code only from a complete Chinese mobile number.
      // Keep other lengths intact so validation rejects them without losing digits.
      this.phoneNumber = digits.replace(/^(?:0086|86)(1[3-9]\d{9})$/, '$1');
      event.target.value = this.phoneNumber;
    },
    digitsOnly(value, maxLength) {
      return String(value || '').replace(/\D/g, '').slice(0, maxLength);
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response && error.response.data;
      if (typeof responseData === 'string') {
        try {
          const parsed = JSON.parse(responseData);
          return (parsed && (parsed.message || parsed.msg || parsed.error)) || fallback;
        } catch (e) {
          return responseData || fallback;
        }
      }
      if (responseData && typeof responseData === 'object') {
        return responseData.message || responseData.msg || responseData.error || fallback;
      }
      return fallback;
    },
    getSmsCode() {
      if (this.smsLoading || this.isSend) {
        return;
      }
      if (!this.phoneNumber) {
        this.$message.error(t("请输入手机号"));
        return;
      }
      if (!(/^1[3456789]\d{9}$/.test(this.phoneNumber))) {
        this.$message.error(t("请输入正确的手机号"));
        return;
      }

      let times = 60;
      this.smsLoading = true;
      delete Axios.defaults.headers.common.Authorization;
      axios
        .post(
          `${process.env.VUE_APP_BASE_URL}/v1/auth/sms-code`,
          null,
          { params: { phone: this.phoneNumber } },
        )
        .then((res) => {
          if (res.data && res.data.error) {
            this.$message.error(res.data.message);
            return;
          }
          this.$message.success(t("验证码已发送"));
          this.isSend = true;
          this.message = `${times}s`;
          timer = setInterval(() => {
            times -= 1;
            this.message = `${times}s`;
            if (times <= 0) {
              clearInterval(timer);
              timer = null;
              this.isSend = false;
              this.message = '';
            }
          }, 1000);
        })
        .catch((error) => {
          this.$message.error(this.getErrorMessage(error, t("验证码发送失败，请稍后重试")));
        })
        .finally(() => {
          this.smsLoading = false;
        });
    },
    login() {
      if (this.loginLoading) {
        return;
      }
      if (!(/^1[3456789]\d{9}$/.test(this.phoneNumber))) {
        this.$message.error(t("请输入正确的手机号"));
        return;
      }
      if (!(/^\d{6}$/.test(this.smsCode))) {
        this.$message.error(t("请输入 6 位验证码"));
        return;
      }

      this.loginLoading = true;
      delete Axios.defaults.headers.common.Authorization;
      axios
        .post(
          `${process.env.VUE_APP_BASE_URL}/v1/auth/login/sms`,
          {
            phone: this.phoneNumber,
            smsCode: this.smsCode,
          },
          { headers: { 'Content-Type': 'application/json' } },
        )
        .then((response) => {
          if (response.data && response.data.error) {
            this.$message.error(response.data.message);
            return;
          }
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          Axios.defaults.headers.common.Authorization = `Bearer ${response.data.accessToken}`;
          this.$message.success(t("登录成功"));
          this.openIssuesPage();
        })
        .catch((error) => {
          this.$message.error(this.getErrorMessage(error, t("手机号不存在")));
        })
        .finally(() => {
          this.loginLoading = false;
        });
    },
    async tryAutoLogin() {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken && !isTokenExpired(accessToken, 10)) {
        Axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        this.openIssuesPage();
        return;
      }

      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (!storedRefreshToken || isTokenExpired(storedRefreshToken, 30)) {
        return;
      }
      try {
        await refreshToken();
        this.$message.success(t("已自动登录"));
        this.openIssuesPage();
      } catch (error) {
        console.warn('Auto login with refresh token failed:', error);
      }
    },
    openIssuesPage() {
      this.$router.replace({ path: '/browser/issues' });
    },
  },
};
</script>

<style scoped>
.login-page {
  --login-navy: #0a3153;
  --login-navy-deep: #082945;
  --login-accent: #0786c9;
  --login-accent-strong: #006da8;
  --login-panel: rgba(255, 255, 255, 0.98);
  --login-field: #f7f9fc;
  --login-field-border: #d7e0ea;
  --login-copy: #142434;
  --login-secondary: #637286;
  --login-line: rgba(255, 255, 255, 0.22);
  position: relative;
  box-sizing: border-box;
  display: grid;
  min-height: 100vh;
  min-height: 100dvh;
  place-items: center;
  overflow: hidden;
  padding: 44px;
  background: #eaf0f5;
  color: var(--login-copy);
  font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", Arial, sans-serif;
}

.login-page *,
.login-page *::before,
.login-page *::after {
  box-sizing: border-box;
}

.login-backdrop {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(43, 83, 113, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(43, 83, 113, 0.055) 1px, transparent 1px);
  background-size: 36px 36px;
  mask-image: linear-gradient(to bottom right, black, transparent 72%);
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(390px, 0.9fr) minmax(460px, 1.1fr);
  width: min(1120px, 100%);
  min-height: min(700px, calc(100dvh - 88px));
  overflow: hidden;
  border: 1px solid rgba(28, 63, 89, 0.13);
  border-radius: 28px;
  background: var(--login-panel);
  box-shadow: 0 28px 80px rgba(17, 50, 75, 0.18);
}

.brand-panel {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  padding: 52px;
  background: var(--login-navy);
  color: #fff;
}

.brand-panel::before,
.brand-panel::after {
  position: absolute;
  z-index: -1;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  content: "";
}

.brand-panel::before {
  right: -190px;
  bottom: -180px;
  width: 470px;
  height: 470px;
  box-shadow: 0 0 0 56px rgba(255, 255, 255, 0.025);
}

.brand-panel::after {
  top: 168px;
  right: -34px;
  width: 112px;
  height: 112px;
  background: rgba(7, 134, 201, 0.22);
}

.brand-panel__content {
  display: flex;
  min-height: 100%;
  flex-direction: column;
}

.brand-lockup {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-logo {
  width: 66px;
  height: 66px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 17px;
  box-shadow: 0 10px 28px rgba(2, 22, 38, 0.22);
}

.brand-name,
.brand-name-cn {
  margin: 0;
}

.brand-name {
  font-size: 1.06rem;
  font-weight: 750;
  letter-spacing: 0.15em;
}

.brand-name-cn {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.875rem;
  letter-spacing: 0.22em;
}

.brand-copy {
  margin-top: auto;
  margin-bottom: auto;
  padding: 66px 0 48px;
}

.brand-eyebrow {
  margin: 0 0 18px;
  color: #79cbf1;
  font-size: 0.875rem;
  font-weight: 650;
  letter-spacing: 0.18em;
}

.brand-copy h1 {
  margin: 0;
  font-size: clamp(2.1rem, 3.3vw, 3.3rem);
  font-weight: 680;
  letter-spacing: -0.035em;
  line-height: 1.25;
}

.brand-description {
  max-width: 360px;
  margin: 24px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 1rem;
  line-height: 1.8;
}

.improvement-path {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--login-line);
  list-style: none;
}

.improvement-path li {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 8px 0 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.875rem;
}

.improvement-path li::before {
  position: absolute;
  top: -4px;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #69cef7;
  box-shadow: 0 0 0 5px rgba(105, 206, 247, 0.12);
  content: "";
}

.path-index {
  color: rgba(255, 255, 255, 0.43);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
}

.login-panel {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 56px clamp(52px, 6vw, 86px) 30px;
}

.mobile-brand {
  display: none;
}

.login-card {
  width: min(100%, 430px);
  margin: auto;
}

.login-heading {
  margin-bottom: 38px;
}

.login-kicker {
  margin: 0 0 10px;
  color: var(--login-accent-strong);
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.login-heading h2 {
  margin: 0;
  color: var(--login-copy);
  font-size: clamp(2rem, 4vw, 2.6rem);
  font-weight: 720;
  letter-spacing: -0.035em;
  line-height: 1.25;
}

.login-heading > p:last-child {
  margin: 14px 0 0;
  color: var(--login-secondary);
  font-size: 1rem;
  line-height: 1.65;
}

.login-form {
  display: grid;
  gap: 24px;
}

.field-group {
  display: grid;
  gap: 10px;
}

.field-label {
  color: var(--login-copy);
  font-size: 0.9rem;
  font-weight: 650;
}

.field-control {
  display: flex;
  height: 56px;
  align-items: center;
  gap: 12px;
  padding: 0 17px;
  border: 1px solid var(--login-field-border);
  border-radius: 14px;
  background: var(--login-field);
  color: #718197;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.field-control:focus-within {
  border-color: var(--login-accent);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(7, 134, 201, 0.11);
}

.field-control > i {
  flex: 0 0 auto;
  font-size: 1.2rem;
}

.field-control input {
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--login-copy);
  font: inherit;
  font-size: 1rem;
}

.field-control input::placeholder {
  color: #9aa6b6;
}

.field-control--code {
  padding-right: 8px;
}

.sms-button {
  display: inline-flex;
  min-width: 108px;
  min-height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  border: 0;
  border-radius: 10px;
  background: rgba(7, 134, 201, 0.1);
  color: var(--login-accent-strong);
  font-size: 0.875rem;
  font-weight: 650;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.sms-button:hover:not(:disabled) {
  background: rgba(7, 134, 201, 0.17);
}

.sms-button:active:not(:disabled) {
  transform: translateY(1px);
}

.sms-button:disabled {
  color: #8492a5;
  cursor: not-allowed;
  opacity: 0.78;
}

.login-button {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
  border: 0;
  border-radius: 14px;
  background: var(--login-navy);
  box-shadow: 0 14px 28px rgba(10, 49, 83, 0.2);
  color: #fff;
  font-size: 1rem;
  font-weight: 680;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.login-button:hover:not(:disabled) {
  background: var(--login-navy-deep);
  box-shadow: 0 16px 32px rgba(10, 49, 83, 0.27);
  transform: translateY(-1px);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  cursor: wait;
  opacity: 0.78;
}

.sms-button:focus-visible,
.login-button:focus-visible,
.login-footer a:focus-visible {
  outline: 3px solid rgba(7, 134, 201, 0.32);
  outline-offset: 3px;
}

.login-note {
  margin: 22px 0 0;
  color: var(--login-secondary);
  font-size: 0.8125rem;
  line-height: 1.6;
  text-align: center;
}

.login-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 34px;
  color: var(--login-secondary);
  font-size: 0.8125rem;
}

.login-footer a {
  color: inherit;
  text-decoration: none;
}

.login-footer a:hover {
  color: var(--login-accent-strong);
}

.footer-divider {
  width: 1px;
  height: 12px;
  background: var(--login-field-border);
}

html[data-theme='dark'] .login-page {
  --login-panel: rgba(19, 28, 40, 0.98);
  --login-field: #182332;
  --login-field-border: #334155;
  --login-copy: #edf3f8;
  --login-secondary: #a3b0c0;
  --login-accent: #48b8ec;
  --login-accent-strong: #65c8f4;
  background: #0d1621;
}

html[data-theme='dark'] .field-control:focus-within {
  background: #16202d;
  box-shadow: 0 0 0 4px rgba(72, 184, 236, 0.13);
}

html[data-theme='dark'] .field-control input::placeholder {
  color: #728094;
}

html[data-theme='dark'] .login-button {
  background: #087bae;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.32);
}

html[data-theme='dark'] .login-button:hover:not(:disabled) {
  background: #0a8ec8;
}

@media (max-width: 900px) {
  .login-page {
    padding: 26px;
  }

  .login-shell {
    display: block;
    width: min(560px, 100%);
    min-height: auto;
  }

  .brand-panel {
    display: none;
  }

  .login-panel {
    min-height: min(700px, calc(100dvh - 52px));
    padding: 36px 52px 28px;
  }

  .mobile-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 48px;
  }

  .mobile-brand img {
    width: 50px;
    height: 50px;
    border-radius: 13px;
  }

  .mobile-brand div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mobile-brand strong {
    color: var(--login-copy);
    font-size: 0.94rem;
    letter-spacing: 0.12em;
  }

  .mobile-brand span {
    color: var(--login-secondary);
    font-size: 0.75rem;
    letter-spacing: 0.16em;
  }

  .login-heading {
    margin-bottom: 34px;
  }
}

@media (max-width: 560px) {
  .login-page {
    align-items: start;
    padding: 0;
    background: var(--login-panel);
  }

  .login-backdrop {
    display: none;
  }

  .login-shell {
    min-height: 100dvh;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .login-panel {
    min-height: 100dvh;
    padding: 28px 24px 22px;
  }

  .mobile-brand {
    margin-bottom: 44px;
  }

  .login-heading h2 {
    font-size: 2rem;
  }

  .login-heading > p:last-child {
    font-size: 0.9375rem;
  }

  .field-control {
    height: 54px;
  }

  .sms-button {
    min-width: 100px;
    padding: 0 10px;
  }

  .login-footer {
    flex-wrap: wrap;
    padding-top: 44px;
  }
}

@media (max-height: 700px) and (min-width: 901px) {
  .login-page {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .login-shell {
    min-height: 620px;
  }

  .brand-copy {
    padding-top: 42px;
    padding-bottom: 36px;
  }

  .login-heading {
    margin-bottom: 28px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .field-control,
  .sms-button,
  .login-button {
    transition: none;
  }
}
</style>
